import { useState } from "react";
import { FlaskConical, Trophy, RotateCcw } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Slider } from "../../components/ui/slider";
import { Button } from "../../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const pct = (n, d) => (d ? `${((n / d) * 100).toFixed(1)}%` : "—");

const leader = (s) => {
  const key = s.A.signups || s.B.signups ? "signups" : "clicks";
  const rate = (v) => (s[v].views ? s[v][key] / s[v].views : 0);
  if (!s.A.views || !s.B.views || rate("A") === rate("B")) return "";
  return rate("A") > rate("B") ? "A" : "B";
};

const Results = ({ stats, onPick }) => {
  const lead = leader(stats);
  return (
    <div className="overflow-x-auto" data-testid="ab-results">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-400 uppercase tracking-wide">
          <th className="pb-2">Version</th><th className="pb-2">Views</th><th className="pb-2">Button taps</th><th className="pb-2">Sign-ups</th><th className="pb-2">Sign-up rate</th><th />
        </tr></thead>
        <tbody>
          {["A", "B"].map((v) => (
            <tr key={v} className="border-t border-slate-100" data-testid={`ab-row-${v}`}>
              <td className="py-2.5 font-bold text-[#12234A]">{v} {lead === v && <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px]" data-testid={`ab-leading-${v}`}><Trophy size={11} /> Leading</span>}</td>
              <td className="py-2.5" data-testid={`ab-views-${v}`}>{stats[v].views}</td>
              <td className="py-2.5" data-testid={`ab-clicks-${v}`}>{stats[v].clicks} <span className="text-slate-400 text-xs">({pct(stats[v].clicks, stats[v].views)})</span></td>
              <td className="py-2.5" data-testid={`ab-signups-${v}`}>{stats[v].signups}</td>
              <td className="py-2.5 font-semibold" data-testid={`ab-rate-${v}`}>{pct(stats[v].signups, stats[v].views)}</td>
              <td className="py-2.5 text-right"><Button size="sm" variant="outline" onClick={() => onPick(v)} className="rounded-lg h-8" data-testid={`ab-use-${v}`}>Use version {v}</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-400 mt-2">“Leading” compares sign-ups per view (or button taps when there are no sign-ups). Wait for a few hundred views before trusting small differences.</p>
    </div>
  );
};

const History = ({ items }) => !items?.length ? null : (
  <div data-testid="ab-history">
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Past tests</p>
    <ul className="space-y-1 text-xs text-slate-500">
      {items.map((h) => (
        <li key={h.ab_id} data-testid={`ab-history-${h.ab_id}`}>
          {h.ended_at?.slice(0, 10)} · <b className="text-[#12234A]">Version {h.winner} chosen</b> · A {h.stats.A.signups}/{h.stats.A.views} sign-ups, B {h.stats.B.signups}/{h.stats.B.views}
          {h.title_b && <> · B: “{h.title_b}”</>}
        </li>
      ))}
    </ul>
  </div>
);

export function PopupAbTest({ form, set, editing, setEditing, onReset, onWinner, saving }) {
  const [pick, setPick] = useState(null);
  const toggle = (on) => {
    set("ab_enabled", on);
    if (on && !(form.variant_b.title || form.variant_b.body || form.variant_b.image || form.variant_b.signup_enabled)) {
      set("variant_b", Object.fromEntries(Object.keys(form.variant_b).map((k) => [k, form[k]])));
    }
    if (!on) setEditing("A");
  };
  const running = form.ab_enabled && form.ab_stats;
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-4" data-testid="popup-ab">
      <label className="flex items-start gap-3 cursor-pointer">
        <Switch checked={form.ab_enabled} onCheckedChange={toggle} data-testid="popup-ab-enabled" className="mt-0.5" />
        <span>
          <span className="flex items-center gap-1.5 text-sm font-semibold text-[#12234A]"><FlaskConical size={15} className="text-[#F5551E]" /> A/B test two versions</span>
          <span className="block text-xs text-slate-500">Visitors are split between version A and version B. Each visitor keeps seeing the same version.</span>
        </span>
      </label>
      {form.ab_enabled && (
        <>
          <div>
            <div className="flex justify-between text-xs font-semibold text-[#12234A] mb-2">
              <span data-testid="popup-ab-split-a">Version A: {100 - form.ab_split}%</span>
              <span data-testid="popup-ab-split-b">Version B: {form.ab_split}%</span>
            </div>
            <Slider min={5} max={95} step={5} value={[form.ab_split]} onValueChange={([v]) => set("ab_split", v)} data-testid="popup-ab-split" />
          </div>
          <div className="flex rounded-xl border border-slate-200 overflow-hidden w-fit text-sm" data-testid="popup-ab-editing">
            {["A", "B"].map((v) => (
              <button key={v} onClick={() => setEditing(v)} data-testid={`popup-ab-edit-${v}`}
                className={`px-4 py-1.5 font-semibold transition-colors ${editing === v ? "bg-[#12234A] text-white" : "text-slate-500 hover:bg-slate-50"}`}>Edit version {v}</button>
            ))}
          </div>
          {running ? (
            <>
              <Results stats={form.ab_stats} onPick={setPick} />
              <Button variant="ghost" size="sm" onClick={onReset} disabled={saving} className="gap-1.5 text-slate-500" data-testid="popup-ab-reset"><RotateCcw size={13} /> Save & restart test (numbers back to zero)</Button>
            </>
          ) : <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="popup-ab-not-started">Save the popup to start the test. Results will appear here.</p>}
        </>
      )}
      <History items={form.ab_history} />
      <AlertDialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <AlertDialogContent data-testid="ab-winner-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Use version {pick} for everyone?</AlertDialogTitle>
            <AlertDialogDescription>
              The test ends and all visitors see version {pick}.{pick === "B" ? " Version B's wording, image and buttons replace version A." : ""} The results are kept under “Past tests”. Unsaved edits on this page will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ab-winner-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { onWinner(pick); setPick(null); }} className="bg-[#F5551E] hover:bg-[#e04812]" data-testid="ab-winner-confirm">Use version {pick}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
